import { BlockEmbed } from 'quill/blots/block';
import type { Delta } from 'quill/core';
import Quill from 'quill/core';
import React, { useEffect, useRef, useState } from 'react';
import EmojiAutocompleter from '../../controls/EmojiAutocompleter';
import MentionPicker from '../../controls/MentionPicker';
import SlashCommandMenu from '../../controls/SlashCommandMenu';
import EditorToolbar from '../../controls/toolbar/EditorToolbar';
import LinkCursorSelection from '../../LinkCursorSelection';
import QuillComponent from '../../QuillComponent';
import { useQuill, withQuillContextProvider } from '../../QuillContext';
import useLinkShortcut from '../../useLinkShortcut';
import containSelection from './containSelection';
import styles from './TableCellEditor.module.scss';
import useCellKeyboardShortcuts from './useCellKeyboardShortcuts';

const DEFAULT_CONTENT = [{ insert: '\n' }];

interface TableCellEditorProps {
  focused: boolean;
  readOnly: boolean;
  containerClassName?: string;
  initialContent?: Delta;
  onContentChange: (delta: Delta) => void;
  onSelectionChange: (range: { index: number; length: number } | null) => void;
}

const TableCellEditor = ({
  focused,
  readOnly,
  containerClassName,
  initialContent,
  onContentChange,
  onSelectionChange,
}: TableCellEditorProps) => {
  const [isInited, setIsInited] = useState(focused);
  const contentRef = useRef(initialContent || DEFAULT_CONTENT);
  const { ancestorQuill, quill } = useQuill();
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (focused) {
      setIsInited(true);
    } else if (quill) {
      quill.changeActiveBlot(null);
    }
  }, [focused, quill]);

  useLinkShortcut();
  useCellKeyboardShortcuts();

  return (
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    <div
      ref={containerRef}
      className={styles.container}
      onClick={(event) => {
        // On click, we should ensure the cell editor contains the selection.
        // Otherwise, cellQuill.getSelection() will return null.
        // An example is in Chrome, triple-click will have the focus node of the
        // selection to be the parent element of the cell editor.

        // contenteditable="true" behaves like `user-select: contain` by default
        // so we don't need to worry about it.
        if (!readOnly) return;

        const root = quill?.root;
        if (!root) return;
        if (!(event.target instanceof Element)) return;

        // Skip if the event is fired from inside a React portal.
        if (!containerRef.current?.contains(event.target)) return;

        const blot = quill.scroll.find(event.target, true);
        if (blot instanceof BlockEmbed) return;

        containSelection(root);
      }}
    >
      {quill && ancestorQuill && (
        <EditorToolbar readOnly={readOnly} portal={ancestorQuill.container} />
      )}
      {isInited && quill && (
        <>
          <MentionPicker quill={quill} readOnly={false} />
          <EmojiAutocompleter quill={quill} readOnly={false} />
        </>
      )}
      <QuillComponent
        noTitleLine
        disallowOrphanFormats={['diff', 'comment']}
        content={contentRef.current}
        namespace="tableCell"
        contentPlaceholder=""
        containerClassName={containerClassName}
        readOnly={readOnly}
        moduleConfig={{
          prefixListener: true,
          history: {
            parentQuill: ancestorQuill,
          },
        }}
        theme="tableCellEditorTheme"
        onSelectionChange={onSelectionChange}
        onTextChange={(change: Delta, _oldContent: Delta, source: string) => {
          if (source === Quill.sources.USER) {
            onContentChange(change);
          }
        }}
      />
      {isInited && quill && (
        <>
          <SlashCommandMenu
            quill={quill}
            readOnly={false}
            imageTooltip="Paste or drag/drop the image into this cell"
          />
          <LinkCursorSelection />
        </>
      )}
    </div>
  );
};

export default withQuillContextProvider(TableCellEditor);
